import { ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ux/Toast";
import { ConfirmProvider } from "./components/ConfirmModal";
import { EventStateProvider } from "./lib/eventState";
import { InterestedCelebrationProvider } from "./lib/interestedCelebration";
import "./styles/index.css";
import { AuthProvider, PlatformProvider } from "./lib/platform";
import { TrackingInitializer } from "./lib/tracking";
import { PostHogProvider } from "posthog-js/react";
import { forceParameterFeatureFlags } from "./lib/features";
import { TranslationProvider } from "./lib/i18n/TranslationProvider";
import { LanguageSyncInitializer } from "./lib/i18n/LanguageSyncInitializer";
import { PostHogConfig } from "posthog-js";
import {
  ConsentProvider,
  markPostHogPageviewCaptured,
  readStoredConsentState,
} from "./lib/consent";
import CookieConsent from "./components/consent/CookieConsent";

// A deploy replaces all hashed chunks on Pages, so sessions loaded before it
// 404 when they lazy-load a route. Vite surfaces that as `vite:preloadError`;
// one reload picks up the new build. The sessionStorage guard prevents a
// reload loop if the failure isn't deploy-related, and is keyed by chunk URL
// so a later, unrelated chunk failure in the same session still recovers.
window.addEventListener("vite:preloadError", (event) => {
  const failedUrl =
    (event as unknown as { payload?: Error }).payload?.message ?? "unknown";
  const guardKey = `chunk-reload:${failedUrl}`;
  if (sessionStorage.getItem(guardKey)) return;
  sessionStorage.setItem(guardKey, "1");
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// PostHog is a bundled SDK, not a <script> tag, so no consent tool can block it
// for us — it has to opt itself out. The cookie is read synchronously here
// because init happens before ConsentProvider mounts, and a single captured
// event before that would defeat the point.
//
// With no stored choice we start opted out and in memory persistence, then
// ConsentProvider grants or withholds once Cloudflare has told us the region.
// For the opt-out majority that grant lands within a few milliseconds, and
// applyConsentToVendors replays the pageview that was suppressed in between.
const storedConsent = readStoredConsentState();
const analyticsConsented = storedConsent?.analytics === true;

const postHogOptions: Partial<PostHogConfig> = {
  api_host: 'https://r.outclose.com', // your managed reverse proxy domain
  ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
  defaults: '2026-05-30',
  autocapture: false,
  person_profiles: 'identified_only',
  // Landing pages live on outclose.com while the quiz/checkout live on
  // app.outclose.com. Writing the cookie on the shared parent domain keeps one
  // distinct_id (and therefore one experiment variant) across that hop —
  // without it, every landing experiment loses its users at signup.
  cross_subdomain_cookie: true,
  opt_out_capturing_by_default: !analyticsConsented,
  persistence: analyticsConsented ? 'localStorage+cookie' : 'memory',
  loaded: (posthog) => {
    if (analyticsConsented) markPostHogPageviewCaptured();
    forceParameterFeatureFlags(posthog);
  },
};

// Mounts one deployable's root component under the full provider stack. Every
// entry in src/entry/ (and the mono main.tsx) goes through here so the apps
// stay behaviorally identical; per-app slimming of the stack can come later.
export function renderRoot(root: ReactNode) {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
    <TranslationProvider>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <PostHogProvider
              apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
              options={postHogOptions}
            >
              {/* Inside PostHogProvider so posthog.init() has already run by the
                  time ConsentProvider's effect opts it in or out. */}
              <ConsentProvider>
                <PlatformProvider>
                  <TrackingInitializer>
                    <LanguageSyncInitializer>
                      <ConfirmProvider>
                        <EventStateProvider>
                          <InterestedCelebrationProvider>
                            {root}
                            <CookieConsent />
                          </InterestedCelebrationProvider>
                        </EventStateProvider>
                      </ConfirmProvider>
                    </LanguageSyncInitializer>
                  </TrackingInitializer>
                </PlatformProvider>
              </ConsentProvider>
            </PostHogProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ToastProvider>
    </TranslationProvider>
    </ErrorBoundary>,
  );
}
